import { Fragment, useContext, useRef, useState } from "react";
import { ParagraphText } from "../../../Libraries";
import { LanguageContext } from "../../../context/LanguageContext";
import { formatNum, uuid } from "../../../oscar-pos-core/constants";
import { Box, capitalize, CircularProgress } from "@mui/material";
import { PopoverMenu, ReceiptPage } from "../../common";
import AddContactSelector from "../../common/AddContactSelector";
import { ListTableAmountCell } from "../../common";
import {
  BlackCheckIcon,
  DeleteNewIcon,
  DownloadIcon,
  EditIcon,
} from "../../../assets/icons/icon";
import { useAuthContext } from "../../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import SidebarBodyContent from "../../common/UBRightSidebar/SidebarBodyContent";
import {
  bulkDeleteOrders,
  bulkRestoreStock,
} from "../../../database/helpers/orders";
import { bulkDeleteInventory } from "../../../database/helpers/inventory";
import {
  bulkCreatePayments,
  bulkDeletePaymentsV2,
  getPaymentsByInvoiceExternal,
} from "../../../database/helpers/payment";
import {
  calculateTotalOutstanding,
  findCustomerByID,
} from "../../../database/helpers/customer";
import { Sync } from "../../../SyncService";
import { useSnackbar } from "../../../context/SnackbarContext";
import withObservables from "@nozbe/with-observables";
import { handleDownloadInvoice } from "../../Features/NewInvoiceBook/invoiceBookUtils";
import { analytics, triggerLogEvent } from "../../../config/firebase";
import { catchError, of } from "rxjs";
import styles from "./styles.module.css";
import ConfirmationModal from "../../common/Modals/ConfirmationModal";
import RecordPaymentModal from "../../common/Modals/RecordPaymentModal";
import { dateFormatInNumberWithSpace } from "../../../utils";
import AttachContactMessage from "../../common/AttachContactMessage";

const ItemList = ({
  sale,
  key,
  index,
  orders,
  customerDetail,
  contactList,
  selectedContactId,
  setSelectedContactId,
  justClosedDropdownRef,
  onEdit = () => {},
  refetchList = () => {},
}) => {
  const addedCustomer = customerDetail || undefined;
  const [I18n] = useContext(LanguageContext);
  const [openDetailSidebar, setOpenDetailSidebar] = useState(false);
  const [htmlContent, setHtmlContent] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const { user, business } = useAuthContext();
  const navigate = useNavigate();
  const componentRef = useRef(); // To reference the content to print
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoadingRecordPayment, setIsLoadingRecordPayment] = useState(false);
  const [isOpenRecordPaymentModal, setIsOpenRecordPaymentModal] =
    useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedSale, setSelectedSale] = useState("");

  const { showSnackbar } = useSnackbar();

  const handleDownload = () => {
    setIsLoading(true);
    try {
      handleDownloadInvoice(
        sale,
        business,
        (error) => {
          if (error) {
            if (!user?.phone_number) {
              showSnackbar({
                message: I18n.report_download_is_not_available_yet,
                severity: "error",
              });
            } else {
              showSnackbar({
                message: I18n.error_downloading_report,
                severity: "error",
              });
            }
            setIsLoading(false);
            return;
          }

          setIsLoading(false)
          showSnackbar({
            message: I18n.successfully_download_invoice,
            severity: "success",
          });
        },
        navigate,
        "Rupin Sale Invoice",
      );
      triggerLogEvent(analytics, "sale_option_download");
    } catch (err) {
      setIsLoading(false);
    }
  };

  const AccoountActions = [
    ...(sale?.tranType?.toLowerCase() != "pos"
      ? [
          {
            label: (
              <Fragment>
                <EditIcon width={20} height={20} />
                <ParagraphText className="textCapitalizeFirstLetter">
                  {I18n.edit}
                </ParagraphText>
              </Fragment>
            ),
            hideOnPaid: true,
            action: (e) => {
              e.stopPropagation();
              triggerLogEvent(analytics, "sale_option_edit");
              onEdit();
            },
          },
        ]
      : []),
    {
      label: (
        <Fragment>
          <DownloadIcon width={20} height={18} id="download_black_btn" />
          <ParagraphText className="textCapitalizeFirstLetter">
            {I18n.download_invoice}
          </ParagraphText>
        </Fragment>
      ),
      action: (e) => {
        e.stopPropagation();
        triggerLogEvent(analytics, "sale_option_download");
        handleDownload();
      },
    },
    {
      label: (
        <ReceiptPage
          invoiceId={sale?.id ?? ""}
          isPrintIcon={false}
          isPrintBtn={false}
          handleDownloadLogEvent={() =>
            triggerLogEvent(analytics, "invoice_receipt_download")
          }
          setIsReceiptLoading={setIsLoading}
          receiptFileName="Rupin Sale Receipt"
        />
      ),
      hideOnUnPaid: true,
      action: (e) => {
        e.stopPropagation();
        triggerLogEvent(analytics, "sale_option_download");
      },
    },
    {
      label: (
        <Fragment>
          <BlackCheckIcon width={16} height={16} id="record_payment_btn" />
          <ParagraphText className="textCapitalizeFirstLetter">
            {I18n.record_payment}
          </ParagraphText>
        </Fragment>
      ),
      hideOnPaid: true,
      action: (e) => {
        e.stopPropagation();
        triggerLogEvent(analytics, "sale_option_mark_paid");
        handleOpenRecordPaymentModal();
      },
    },
    {
      label: (
        <Fragment>
          <DeleteNewIcon width={20} height={18} id="delete_black_btn" />
          <ParagraphText className="textCapitalizeFirstLetter">
            {I18n.delete}
          </ParagraphText>
        </Fragment>
      ),
      danger: true,
      action: (e) => {
        deletePrompt();
        e.stopPropagation();
      },
    },
  ];

  const deletePrompt = () => {
    setShowDeleteModal(true);
    return;
  };

  const handleDelete = async (e) => {
    e.stopPropagation();
    triggerLogEvent(analytics, "sale_option_delete");
    setShowDeleteModal(false);
    setIsActionLoading(true);

    if (orders.length) {
      let toDelete = [];
      await Promise.all(
        orders.map(async (item) => {
          let stock = await item.stock.fetch();

          toDelete.push({
            original: item,
            updated: stock,
          });
        }),
      );

      if (toDelete.length) {
        await bulkRestoreStock(toDelete, sale.type);
        await bulkDeleteOrders(toDelete);
        const origin =
          sale.tranType === "bill" ? "invoice" : sale.tranType === "pos" ? "pos" : "cash";
        await bulkDeleteInventory(sale, toDelete, sale.type, origin);
      }
    }
    if (customerDetail) {
      let payments = await sale.payment.fetch();
      await bulkDeletePaymentsV2(payments);

      let total = await calculateTotalOutstanding(customerDetail);
      await customerDetail.updateTotalOutstanding(total);
    }

    await sale.markAsDeleted();
    refetchList();
    setOpenDetailSidebar(false);
    setIsActionLoading(false);
    triggerLogEvent(analytics, "sale_deleted");
    Sync(user.phone_number, navigate);
    showSnackbar({
      message: I18n.transaction_deleted,
      severity: "success",
      icon: "",
    });
  };

  const handleSelectCustomer = async (customer) => {
    setSelectedCustomer(customer);
    setSelectedContactId(null);
  };

  const handleAddCustomer = async (customer) => {
    try {
      await sale.updateInvoiceCustomer(customer);

      if (sale?.status?.toLowerCase() != "paid") {
        const data = {
          amount: sale.amount,
          date: sale.transacted_at,
          image_url: sale.image_url,
          note: sale.note,
          advance_amount: sale.advance_amount || 0,
        };
        await handleCreateCustomerPayment(business, customer, data, sale);
      }
      setSearchQuery("");
      setSelectedCustomer("");
      setSelectedContactId("");
      Sync(user.phone_number, navigate);
      showSnackbar({
        message: I18n.contact_attached_successfully,
        severity: "success",
      });
    } catch (error) {
      console.error(error);
      await sale.updateInvoiceCustomer(null);
    }
  };

  const handleOpenRecordPaymentModal = () => {
    setSelectedSale(sale);
    setIsOpenRecordPaymentModal(true);
  };

  const handleRecordPayment = (
    selectedMethod,
    selectedDate,
    receivedAmount,
  ) => {
    if (sale?.tranType?.toLowerCase() === "bill") {
      handleEditInvoice(
        Number(receivedAmount) + Number(sale.advance_amount),
        selectedMethod,
        selectedDate,
        customerDetail,
      );
    } else {
      handleEditQuickSale(
        Number(receivedAmount) + Number(sale.advance_amount),
        selectedMethod,
        selectedDate,
      );
    }
  };

  const handleEditQuickSale = async (
    amountRecvd,
    paymentMethod,
    transaction_date,
  ) => {
    setIsLoadingRecordPayment(true);
    triggerLogEvent(analytics, "sale_record");
    const invoiceAmountObj = {
      discount: sale?.discount || 0,
      discount_unit: sale?.discount_unit || "rs",
      tax_rate: sale?.tax_rate || 0,
      total: 0,
      sub_total: sale?.sub_total || 0,
      advance_amount: sale?.advance_amount || 0,
      profit: sale?.profit || 0,
    };
    let invoiceAmount = Number(sale?.amount) || 0;
    let invoiceStatus =
      invoiceAmount - (Number(amountRecvd) || 0) === 0 ? "paid" : "unpaid";
    let partialAmount = invoiceAmount - (Number(amountRecvd) || 0);
    const currentMethod = !paymentMethod ? sale?.method : paymentMethod;

    const fetchPaidTo = selectedSale?.customer?.id
      ? await findCustomerByID(selectedSale?.customer?.id)
      : null;

    const dataToEdit = {
      customer: selectedSale?.customer?.id ? fetchPaidTo : null,
      recording_uri: sale?.recording_uri,
      advance_amount: Number(amountRecvd) || 0,
      invoice_number: sale?.invoice_number || business.invoice_number_count,
      invoice_prefix: sale?.invoice_prefix || business.invoice_number_prefix,
      invoice_title: sale?.invoice_title,
      discount_unit: sale?.discount_unit,
      sales_amount: sale?.sales_amount,
      document_url: sale?.document_url,
      cost_amount: sale?.cost_amount,
      attachment: sale?.attachment,
      sub_total: sale?.sub_total,
      signature: sale?.signature,
      tax_title: sale?.tax_title,
      tranType: sale?.tranType,
      image_url: sale?.image || null,
      date: transaction_date
        ? transaction_date
        : sale.transacted_at || new Date(),
      category: sale?.category,
      discount: sale?.discount,
      tax_rate: sale?.tax_rate,
      amount: invoiceAmount,
      method: currentMethod,
      wallet: currentMethod === "wallet" ? paymentMethod?.name : null,
      profit: sale?.profit,
      status: invoiceStatus,
      total: Number(invoiceAmountObj?.total) || 0,
      note: sale?.note || "",
      bank: currentMethod === "bank" ? paymentMethod?.name : null,
      partialAmount,
    };
    if (dataToEdit.customer) {
      let dataForPayment = {
        udhaar_note: dataToEdit?.note,
        date: dataToEdit?.date || new Date(),
        image_url: dataToEdit?.image_url || null,
        invoiceDetail: sale,
        type: "cashbook",
      };
      // if total amount is changed wrt to current invoice, we will create or update invoice full payment entry
      if (invoiceAmount != sale?.amount) {
        dataForPayment["amount"] = invoiceAmount;
        let invoicePayment = await getPaymentsByInvoiceExternal(
          sale?.id,
          "full_invoice",
        );

        if (invoicePayment.length) {
          await invoicePayment[0]?.updatePayment(dataForPayment);
        } else {
          await dataToEdit.customer.createPayment({
            ...dataForPayment,
            uid: uuid(),
            date: new Date(),
            payment_mode: "Jama",
            external_type: "full_invoice",
          });
        }
      }
      if (Number(sale?.advance_amount) != Number(dataToEdit?.advance_amount)) {
        // if received or partial amount is changed wrt to current invoice, we will create or update invoice partial payment entry

        dataForPayment["amount"] = Number(dataToEdit?.advance_amount);

        let invoicePayment = await getPaymentsByInvoiceExternal(
          sale?.id,
          "partial_invoice",
        );

        if (invoicePayment.length) {
          await invoicePayment[0]?.updatePayment(dataForPayment);
        } else {
          let payObj = {
            date: dataToEdit?.date || new Date(),
            note: dataToEdit?.note || "",
            image_url: dataToEdit?.image_url || "",
            type: "cashbook",
            uid: uuid(),
            amount: Number(dataToEdit.advance_amount),
            payment_mode: "Jama",
            external_type: "partial_invoice",
          };

          await bulkCreatePayments(business, dataToEdit.customer, sale, [
            payObj,
          ]);
        }
      }

      let totalOutstanding = await calculateTotalOutstanding(
        dataToEdit.customer,
      );

      await dataToEdit.customer.updateTotalOutstanding(totalOutstanding);
    }
    await sale.updateInvocie(dataToEdit);
    if (fetchPaidTo) triggerLogEvent(analytics, "sale_record_name");
    if (dataToEdit.advance_amount)
      triggerLogEvent(analytics, "sale_record_amount");
    if (dataToEdit.method) triggerLogEvent(analytics, "sale_record_method");
    setIsLoadingRecordPayment(false);
    setIsOpenRecordPaymentModal(false);
    setOpenDetailSidebar(false);
    refetchList();
    Sync(user?.phone_number, navigate);
    showSnackbar({
      message: I18n.update_record_sucessfully,
      severity: "success",
    });
  };

  const handleInvoicePaymentMethod = async (data, createdInvoice) => {
    let { method, bank, wallet, advance_amount } = data;

    let params = {
      method: method || "cash",
      bank,
      wallet,
      amount: advance_amount,
    };
    try {
      let invPayments = await createdInvoice?.invoice_payments.fetch();
      if (!invPayments?.length) {
        await createdInvoice.createTransactionPayments(params);
      } else {
        await invPayments[0].updateInvPayment(params);
      }
    } catch (error) {}
  };

  const updateInvoiceData = async (data) => {
    sale.updateInvocie(data).then(async () => {
      await Sync(user?.phone_number, navigate);
    });
  };

  const handleEditInvoice = async (
    amountRecvd,
    paymentMethod,
    selected_date,
    customer,
  ) => {
    const invoiceCount = business.invoice_number_count;
    const invoiceAmountObj = {
      discount: sale?.discount || 0,
      discount_unit: sale?.discount_unit || "rs",
      tax_rate: sale?.tax_rate || 0,
      total: 0,
      sub_total: sale?.sub_total || 0,
      advance_amount: sale?.advance_amount || 0,
      profit: sale?.profit || 0,
    };
    let invoiceAmount = Number(sale?.amount) || 0;
    let invoiceStatus =
      invoiceAmount - (Number(amountRecvd) || 0) === 0 ? "paid" : "unpaid";
    let partialAmount = invoiceAmount - (Number(amountRecvd) || 0);
    const inv_items = await sale.orders.fetch();
    const currentMethod = !paymentMethod ? sale?.method : paymentMethod;

    let invoiceData = {
      sales_amount: invoiceAmount,
      cost_amount: sale?.cost_amount || 0,
      amount: invoiceAmount,
      date: selected_date ? selected_date : sale.transacted_at || new Date(),
      customer: customer,
      items: inv_items || [],
      image_url: sale?.image_url || "",
      document_url: sale?.document_url || "",
      signature: sale?.signature,
      attachment: sale?.attachment || [],
      note: sale?.note,
      status: invoiceStatus,
      method: currentMethod,
      type: sale?.type,
      tranType: sale?.tranType || "bill",
      bank: currentMethod === "bank" ? paymentMethod?.name : sale?.bank || null,
      wallet:
        currentMethod === "wallet" ? paymentMethod?.name : sale?.bank || null,
      invoice_title: sale?.invoice_title,
      invoice_number_count: invoiceCount + 1,
      invoice_number: sale?.invoice_number || business.invoice_number_count,
      invoice_prefix: sale?.invoice_prefix || business.invoice_number_prefix,
      discount: Number(invoiceAmountObj?.discount) || 0,
      discount_unit: invoiceAmountObj?.discount_unit,
      tax_rate: Number(invoiceAmountObj?.tax_rate) || 0,
      total: Number(invoiceAmountObj?.total) || 0,
      sub_total: Number(invoiceAmountObj?.sub_total) || 0,
      advance_amount: Number(amountRecvd) || 0,
      profit: Number(invoiceAmountObj?.profit) || 0,
      partialAmount,
    };
    if (customer && !addedCustomer) {
      await handleCreateCustomerPayment(customer, invoiceData, sale);
    } else if (customer) {
      let dataForPayment = {
        udhaar_note: invoiceData?.note,
        date: invoiceData?.date || new Date(),
        image_url: invoiceData?.image_url || null,
        sale,
        type: "invoicebook",
      };

      if (invoiceAmount != sale?.amount) {
        dataForPayment["amount"] = invoiceAmount;
        let invoicePayment = await getPaymentsByInvoiceExternal(
          sale?.id,
          "full_invoice",
        );

        if (invoicePayment.length) {
          await invoicePayment[0].updatePayment(dataForPayment);
        } else {
          await customer.createPayment({
            ...dataForPayment,
            uid: uuid(),
            date: new Date(),
            payment_mode: "Udhaar",
            external_type: "full_invoice",
          });
        }
      } else if (Number(sale?.advance_amount) != Number(amountRecvd)) {
        dataForPayment["amount"] = Number(amountRecvd);

        let invoicePayment = await getPaymentsByInvoiceExternal(
          sale?.id,
          "partial_invoice",
        );

        if (invoicePayment.length) {
          await invoicePayment[0].updatePayment(dataForPayment);
        } else {
          await customer.createPayment({
            ...dataForPayment,
            uid: uuid(),
            date: new Date(),
            payment_mode: "Jama",
            external_type: "partial_invoice",
          });
        }
      } else if (invoiceStatus == "paid") {
        dataForPayment["amount"] = Number(amountRecvd);

        let invoicePayment = await getPaymentsByInvoiceExternal(
          sale?.id,
          "partial_invoice",
        );

        if (invoicePayment.length) {
          await invoicePayment[0]?.updatePayment(dataForPayment);
        } else {
          await customer.createPayment({
            ...dataForPayment,
            uid: uuid(),
            date: new Date(),
            payment_mode: "Jama",
            external_type: "partial_invoice",
          });
        }
      }

      let totalOutstanding = await calculateTotalOutstanding(customer);

      await customer.updateTotalOutstanding(totalOutstanding);
    }

    await handleInvoicePaymentMethod(invoiceData, sale);

    updateInvoiceData(invoiceData)
      .then((ans) => {
        refetchList();
        triggerLogEvent(analytics, "inv_record_name");
        triggerLogEvent(analytics, "inv_record_method");
        triggerLogEvent(analytics, "inv_record_amount");
        triggerLogEvent(analytics, "inv_record");
        setOpenDetailSidebar(false);
        setIsOpenRecordPaymentModal(false);
        showSnackbar({
          message: I18n.update_record_sucessfully,
          severity: "success",
        });
      })
      .catch((er) => {});
  };

  const handleCreateCustomerPayment = async (
    business,
    customer,
    data,
    invoice,
  ) => {
    let payObj = {
      date: data?.date || new Date(),
      note: data?.note || "",
      image_url: data?.image_url || "",
      type: "cashbook",
    };

    let paymentArr = [
      {
        ...payObj,
        uid: uuid(),
        amount: data.amount,
        payment_mode: "Udhaar",
        external_type: "full_invoice",
      },
    ];

    if (data?.advance_amount > 0) {
      paymentArr.push({
        ...payObj,
        uid: uuid(),
        amount: Number(data.advance_amount),
        payment_mode: "Jama",
        external_type: "partial_invoice",
      });
    }

    await bulkCreatePayments(business, customer, invoice, paymentArr);

    let totalOutstanding = await calculateTotalOutstanding(customer);

    await customer.updateTotalOutstanding(totalOutstanding);
  };

  let capitalizeMethod = sale?.["method"]
    ? capitalize(sale["method"]?.toString())
    : "";

  let capitalizeStatus = sale?.["status"]
    ? capitalize(sale["status"]?.toString())
    : "";

  return (
    <Box
      id={`sale_list_${index}`}
      className="invoiceListTableBody AccountItemListWrapper"
      key={key}
      onClick={(e) => {
        if (
          showDeleteModal ||
          selectedCustomer ||
          openDetailSidebar ||
          isOpenRecordPaymentModal ||
          justClosedDropdownRef.current
        )
          return;

        e.stopPropagation();
        if (selectedContactId) {
          setSelectedContactId("");
          return;
        }
        setOpenDetailSidebar(true);
        triggerLogEvent(analytics, "sale_detail");
      }}
    >
      <Box
        className={`invListTableHeaderCol sevenColumn textLeft sidebarOption`}
      >
        <ParagraphText className="fontSize14 fontWeight400 truncateOneLine textLeft newFontColor">
          {dateFormatInNumberWithSpace(sale?.transacted_at)}
        </ParagraphText>
      </Box>

      <Box
        className={`invListTableHeaderCol sevenColumn textLeft sidebarOption`}
      >
        <ParagraphText className="fontSize14 fontWeight400 truncateOneLine textLeft newFontColor">
          {sale?.tranType == "pos"
            ? I18n.pos
            : sale?.tranType == "bill"
              ? I18n.invoice
              : I18n.quick_sale}
        </ParagraphText>
      </Box>

      <Box className={`invListTableHeaderCol sevenColumn textLeft`}>
        <AddContactSelector
          selectedContact={
            customerDetail?.name
              ? { id: customerDetail?.id, name: customerDetail.name }
              : null
          }
          contacts={contactList || []}
          isOpen={selectedContactId === sale?.id}
          onOpenChange={(open) => {
            if (!customerDetail && !selectedContactId) {
              setSelectedContactId(open ? sale.id : "");
            } else if (selectedContactId) {
              justClosedDropdownRef.current = true;
              setSelectedContactId("");
              setTimeout(() => {
                justClosedDropdownRef.current = false;
              }, 100);
            } else if (!selectedContactId && !justClosedDropdownRef.current) {
              setOpenDetailSidebar(true);
            }
          }}
          onSelect={handleSelectCustomer}
          addContactLabel={I18n.add_contact || "Add contact"}
          searchPlaceholder={I18n.search_contact || "Search contact"}
        />
      </Box>
      <Box className={`invListTableHeaderCol sevenColumn textLeft`}>
        <ParagraphText className="fontSize14 fontWeight400 truncateOneLine textLeft newFontColor displayFlex">
          {(() => {
            const statusText = sale["status"]
              ? capitalizeStatus
              : sale?.amount - (sale?.advance_amount || 0) > 0
                ? "Unpaid"
                : "Paid";

            // Display only: status/method are stored in English, so translate the label here
            const statusLabel = capitalize(I18n[statusText.toLowerCase()] || statusText);
            const methodLabel = capitalizeMethod
              ? capitalize(I18n[String(sale["method"]).toLowerCase()] || capitalizeMethod)
              : "";
            const methodText = methodLabel ? `/ ${methodLabel}` : "";

            return `${statusLabel} ${methodText}`.trim();
          })()}
        </ParagraphText>
      </Box>

      <Box
        className={`invListTableHeaderCol sevenColumn textRight amountColumnNewWrapper`}
      >
        <ListTableAmountCell
          amountWrapperClassName={
            capitalizeStatus == "Paid"
              ? "newFontColor"
              : "foundationOrangeColor"
          }
        >
          {capitalizeStatus == "Paid" && (sale?.amount < 0 ? "-" : "+")}Rs.{" "}
          {formatNum(Math.abs(sale.amount) || 0)}
        </ListTableAmountCell>
      </Box>

      <Box
        className={`invListTableHeaderCol sevenColumn amountColumnNewWrapper`}
      >
        <ParagraphText
          className={`amountColumnInnerWrapper fontSize14 fontWeight400 truncateOneLine newFontColor`}
        >
          Rs.{" "}
          {sale?.status?.toLowerCase() != "paid"
            ? formatNum(Math.abs(sale.amount - (sale?.advance_amount || 0))) ||
              0
            : 0}
        </ParagraphText>
      </Box>

      <Box
        className={`amountColumnInnerWrapper textCenter invListTableHeaderCol sevenColumn`}
      >
        <ParagraphText className="fontSize14 fontWeight400 textCenter newFontColor tableActionsCell">
          <div className="tblOptnBox" role="button">
            <PopoverMenu
              id="sale"
              menuOptions={AccoountActions.filter((acts) => {
                const status = sale?.status?.toLowerCase();

                if (status === "paid" && acts?.hideOnPaid) {
                  return false;
                }

                if ((status === "unpaid" || !status) && acts?.hideOnUnPaid) {
                  return false;
                }

                return true;
              })}
              popOverStyle={{
                marginLeft: "5px",
                borderRadius: "16px !important",
              }}
              popoverButton={
                (isLoading || isActionLoading) && (
                  <Box>
                    <CircularProgress size={"15px"} />
                  </Box>
                )
              }
              disabled={isLoading || isActionLoading}
            />
          </div>
        </ParagraphText>
      </Box>
      {openDetailSidebar ? (
        <SidebarBodyContent
          onClose={() => setOpenDetailSidebar(false)}
          open={openDetailSidebar}
          quickSale={sale}
          refetchList={refetchList}
          business={business}
          handleDelete={() => setShowDeleteModal(true)}
          handleMarkAsPaid={() => {
            triggerLogEvent(
              analytics,
              "sale_side_bar_open_record_payment_modal",
            );
            handleOpenRecordPaymentModal();
          }}
          handleEdit={onEdit}
          isShowEditBtn={sale?.tranType == "pos" ? false : true}
          isShowDeleteBtn={sale?.tranType == "pos" ? false : true}
        />
      ) : null}

      {showDeleteModal && (
        <ConfirmationModal
          open={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDelete}
          title={I18n.this_action_cannot_be_undone}
          message={I18n.are_you_sure_you_want_to_delete_transaction}
          confirmText={I18n.delete_sale}
          id="sale_delete_modal"
        />
      )}

      {selectedCustomer && (
        <ConfirmationModal
          open={Boolean(selectedCustomer)}
          onClose={() => setSelectedCustomer("")}
          onConfirm={() => handleAddCustomer(selectedCustomer)}
          title={I18n.you_wont_be_able_to_change_it_later}
          htmlMessage={
            <AttachContactMessage name={selectedCustomer.name} />
          }
          confirmText={I18n.confirm}
          confirmButtonClassName="ubPrimaryButton"
          id="sale_add_contact_cofirm_modal"
        />
      )}

      {isOpenRecordPaymentModal && sale?.id == selectedSale?.id ? (
        <Box onClick={(e) => e.stopPropagation()}>
          <RecordPaymentModal
            handleConfirm={handleRecordPayment}
            className={styles.modalContainer}
            handleClose={() => setIsOpenRecordPaymentModal(false)}
            isOpen={isOpenRecordPaymentModal}
            title={I18n.record_payment}
            selectedInvoice={sale}
            isLoading={isLoadingRecordPayment}
          />
        </Box>
      ) : null}

      <div style={{ display: "none" }}>
        <div ref={componentRef}>
          <div dangerouslySetInnerHTML={{ __html: htmlContent }} />
        </div>
      </div>
    </Box>
  );
};

const enhance = withObservables(["sale", "business"], ({ sale, business }) => ({
  customerDetail: sale?.customer
    ? sale.customer.observe().pipe(catchError(() => of(null)))
    : of(null),

  orders: sale?.orders ? sale.orders.observe() : of([]),
}));

export default enhance(ItemList);
